import i18n from '@/lib/i18n';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import toast from 'react-hot-toast';
import {
  AdminPermissionMatrix,
  AdminActionType,
  AuditLogEntry,
  StaffPerformanceMetrics,
  KitchenMonitoringData,
  FraudAlert,
  ApprovalRequest,
  SecurityContext,
  DEFAULT_ADMIN_PERMISSIONS,
} from '@/types/admin';

interface AdminState {
  // Permissions & Security
  permissions: AdminPermissionMatrix;
  securityContext: SecurityContext | null;
  isAuthenticated: boolean;
  
  // Audit Logging
  auditLogs: AuditLogEntry[];
  
  // Staff Oversight
  performanceMetrics: StaffPerformanceMetrics[];
  
  // Kitchen Monitoring
  kitchenData: KitchenMonitoringData | null;
  
  // Fraud Prevention
  fraudAlerts: FraudAlert[];
  
  // Approval System
  pendingApprovals: ApprovalRequest[];
  
  // UI State
  isLoading: boolean;
  error: string | null;
}

interface AdminActions {
  // Authentication & Security
  initializeAdminSession: (adminId: string, sessionData: Partial<SecurityContext>) => void;
  terminateAdminSession: () => void;
  updatePermissions: (permissions: Partial<AdminPermissionMatrix>) => void;
  
  // Audit Logging
  logAdminAction: (action: AdminActionType, resource: string, details: Record<string, any>, resourceId?: string) => Promise<void>;
  getAuditLogs: (filters?: {
    adminId?: string;
    action?: AdminActionType;
    dateRange?: { start: Date; end: Date };
    severity?: string;
  }) => AuditLogEntry[];
  
  // Staff Oversight
  getStaffPerformanceMetrics: (employeeId?: string, period?: { start: Date; end: Date }) => StaffPerformanceMetrics[];
  updatePerformanceMetrics: (metrics: StaffPerformanceMetrics) => void;
  flagStaffPerformance: (employeeId: string, flag: { type: string; severity: string; description: string }) => void;
  
  // Kitchen Monitoring (View-Only)
  updateKitchenMonitoring: (data: KitchenMonitoringData) => void;
  getKitchenEfficiencyReport: () => {
    averagePreparationTime: number;
    delayedOrdersPercentage: number;
    kitchenUtilization: number;
    peakHours: { hour: number; orderCount: number }[];
  };
  
  // Order Management (Strategic Level)
  adminCancelOrder: (orderId: string, reason: string) => Promise<void>;
  adminModifyOrder: (orderId: string, modifications: any, reason: string) => Promise<void>;
  adminProcessRefund: (orderId: string, amount: number, reason: string) => Promise<void>;
  
  // Fraud Prevention
  createFraudAlert: (alert: Omit<FraudAlert, 'id' | 'timestamp'>) => void;
  updateFraudAlert: (alertId: string, updates: Partial<FraudAlert>) => void;
  getFraudAlerts: (filters?: { severity?: string; status?: string; type?: string }) => FraudAlert[];
  
  // Approval System
  createApprovalRequest: (request: Omit<ApprovalRequest, 'id' | 'requestedAt'>) => string;
  processApproval: (requestId: string, approved: boolean, reason?: string) => Promise<void>;
  emergencyOverride: (requestId: string, reason: string) => Promise<void>;
  
  // Emergency Controls
  triggerSystemLockdown: (reason: string) => Promise<void>;
  emergencyRefund: (orderId: string, amount: number, reason: string) => Promise<void>;
  
  // Utility
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  checkPermission: (category: keyof AdminPermissionMatrix, permission: string) => boolean;
}

type AdminStore = AdminState & AdminActions;


// Create the admin store
export const useAdminStore = create<AdminStore>()(
  persist(
    immer((set, get) => ({
      // Initial state
      permissions: DEFAULT_ADMIN_PERMISSIONS,
      securityContext: null,
      isAuthenticated: false,
      auditLogs: [],
      performanceMetrics: [], // Don't auto-initialize with mock data
      kitchenData: null,
      fraudAlerts: [], // Don't auto-initialize with mock data
      pendingApprovals: [],
      isLoading: false,
      error: null,

      // Initialize admin session
      initializeAdminSession: (adminId: string, sessionData: Partial<SecurityContext>) => {
        set(draft => {
          draft.securityContext = {
            adminId,
            sessionId: `session-${Date.now()}`,
            ipAddress: '192.168.1.100', // Mock IP
            userAgent: navigator.userAgent,
            loginTime: new Date(),
            lastActivity: new Date(),
            permissions: DEFAULT_ADMIN_PERMISSIONS,
            mfaEnabled: true,
            sessionTimeout: 3600000, // 1 hour
            maxConcurrentSessions: 2,
            requiresReauthentication: false,
            riskScore: 0.1,
            riskFactors: [],
            actionsPerformed: 0,
            sensitiveActionsCount: 0,
            lastAuditReview: new Date(),
            ...sessionData,
          };
          draft.isAuthenticated = true;
        });
      },

      // Terminate admin session
      terminateAdminSession: () => {
        set(draft => {
          draft.securityContext = null;
          draft.isAuthenticated = false;
        });
      },

      // Update permissions
      updatePermissions: (permissions: Partial<AdminPermissionMatrix>) => {
        set(draft => {
          if (draft.securityContext) {
            draft.securityContext.permissions = {
              ...draft.securityContext.permissions,
              ...permissions,
            };
          }
        });
      },

      // Log admin action
      logAdminAction: async (action: AdminActionType, resource: string, details: Record<string, any>, resourceId?: string) => {
        const { securityContext } = get();
        if (!securityContext) return;

        const severity = getSeverityForAction(action);
        const category = getCategoryForAction(action);

        const logEntry: AuditLogEntry = {
          id: `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date(),
          adminId: securityContext.adminId,
          adminName: 'Admin User', // Would get from user store
          action,
          resource,
          resourceId,
          details,
          ipAddress: securityContext.ipAddress,
          userAgent: securityContext.userAgent,
          sessionId: securityContext.sessionId,
          severity,
          category,
          requiresSecondaryApproval: severity === 'critical',
        };

        set(draft => {
          draft.auditLogs.unshift(logEntry);
          if (draft.securityContext) {
            draft.securityContext.actionsPerformed++;
            if (severity === 'high' || severity === 'critical') {
              draft.securityContext.sensitiveActionsCount++;
            }
            draft.securityContext.lastActivity = new Date();
          }
        });

        // Show toast notification for high-severity actions
        if (severity === 'high' || severity === 'critical') {
          toast.success(`Admin action logged: ${action}`, {
            duration: 2000,
            icon: '🔒',
          });
        }
      },

      // Get audit logs with filters
      getAuditLogs: (filters = {}) => {
        const { auditLogs } = get();
        return auditLogs.filter(log => {
          if (filters.adminId && log.adminId !== filters.adminId) return false;
          if (filters.action && log.action !== filters.action) return false;
          if (filters.severity && log.severity !== filters.severity) return false;
          if (filters.dateRange) {
            const logDate = new Date(log.timestamp);
            if (logDate < filters.dateRange.start || logDate > filters.dateRange.end) return false;
          }
          return true;
        });
      },

      // Get staff performance metrics
      getStaffPerformanceMetrics: (employeeId?: string, period?: { start: Date; end: Date }) => {
        const { performanceMetrics } = get();
        return performanceMetrics.filter(metric => {
          if (employeeId && metric.employeeId !== employeeId) return false;
          if (period) {
            if (metric.period.start < period.start || metric.period.end > period.end) return false;
          }
          return true;
        });
      },

      // Update performance metrics
      updatePerformanceMetrics: (metrics: StaffPerformanceMetrics) => {
        set(draft => {
          const index = draft.performanceMetrics.findIndex(m => 
            m.employeeId === metrics.employeeId && 
            m.period.start.getTime() === metrics.period.start.getTime()
          );
          if (index >= 0) {
            draft.performanceMetrics[index] = metrics;
          } else {
            draft.performanceMetrics.push(metrics);
          }
        });
      },

      // Flag staff performance
      flagStaffPerformance: (employeeId: string, flag: { type: string; severity: string; description: string }) => {
        set(draft => {
          const metric = draft.performanceMetrics.find(m => m.employeeId === employeeId);
          if (metric) {
            metric.flags.push({
              type: flag.type as any,
              severity: flag.severity as any,
              description: flag.description,
              flaggedAt: new Date(),
            });
          }
        });
      },

      // Update kitchen monitoring data
      updateKitchenMonitoring: (data: KitchenMonitoringData) => {
        set(draft => {
          draft.kitchenData = data;
        });
      },

      // Get kitchen efficiency report
      getKitchenEfficiencyReport: () => {
        const { kitchenData } = get();
        if (!kitchenData) {
          return {
            averagePreparationTime: 0,
            delayedOrdersPercentage: 0,
            kitchenUtilization: 0,
            peakHours: [],
          };
        }

        return {
          averagePreparationTime: kitchenData.averagePreparationTime,
          delayedOrdersPercentage: (kitchenData.delayedOrders / kitchenData.activeOrders) * 100,
          kitchenUtilization: kitchenData.kitchenEfficiency,
          peakHours: [], // Would calculate from historical data
        };
      },

      // Admin cancel order
      adminCancelOrder: async (orderId: string, reason: string) => {
        const { logAdminAction } = get();

        try {
          const { apiClient } = await import('@/lib/api');
          await apiClient.post(`/orders/${orderId}/cancel`, { reason });

          await logAdminAction(AdminActionType.CANCEL_ORDER, 'order', {
            orderId,
            reason,
            cancelledAt: new Date(),
          }, orderId);

          toast.success(`Order ${orderId} cancelled successfully`, {
            duration: 3000,
            icon: '❌',
          });
        } catch (error) {
          toast.error(i18n.t('admin.toast.cancelOrderFailed'));
        }
      },

      // Admin modify order
      adminModifyOrder: async (orderId: string, modifications: any, reason: string) => {
        const { logAdminAction } = get();

        try {
          const { apiClient } = await import('@/lib/api');
          await apiClient.put(`/orders/${orderId}`, { ...modifications, notes: reason });

          await logAdminAction(AdminActionType.MODIFY_ORDER, 'order', {
            orderId,
            modifications,
            reason,
            modifiedAt: new Date(),
          }, orderId);

          toast.success(`Order ${orderId} modified successfully`, {
            duration: 3000,
            icon: '✏️',
          });
        } catch (error) {
          toast.error(i18n.t('admin.toast.modifyOrderFailed'));
        }
      },

      // Admin process refund
      adminProcessRefund: async (orderId: string, amount: number, reason: string) => {
        const { logAdminAction } = get();

        try {
          const { apiClient } = await import('@/lib/api');
          // Cancel the order with refund reason — dedicated refund endpoint can be added later
          await apiClient.post(`/orders/${orderId}/cancel`, { reason: `REFUND: ${reason} (amount: ${amount})` });

          await logAdminAction(AdminActionType.PROCESS_REFUND, 'order', {
            orderId,
            amount,
            reason,
            refundedAt: new Date(),
          }, orderId);

          toast.success(`Refund of $${amount} processed for order ${orderId}`, {
            duration: 3000,
            icon: '💰',
          });
        } catch (error) {
          toast.error(i18n.t('admin.toast.refundFailed'));
        }
      },

      // Create fraud alert
      createFraudAlert: (alert: Omit<FraudAlert, 'id' | 'timestamp'>) => {
        set(draft => {
          const newAlert: FraudAlert = {
            ...alert,
            id: `fraud-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            timestamp: new Date(),
          };
          draft.fraudAlerts.unshift(newAlert);
        });
      },

      // Update fraud alert
      updateFraudAlert: (alertId: string, updates: Partial<FraudAlert>) => {
        set(draft => {
          const index = draft.fraudAlerts.findIndex(alert => alert.id === alertId);
          if (index >= 0) {
            Object.assign(draft.fraudAlerts[index], updates);
          }
        });
      },

      // Get fraud alerts with filters
      getFraudAlerts: (filters = {}) => {
        const { fraudAlerts } = get();
        return fraudAlerts.filter(alert => {
          if (filters.severity && alert.severity !== filters.severity) return false;
          if (filters.status && alert.status !== filters.status) return false;
          if (filters.type && alert.type !== filters.type) return false;
          return true;
        });
      },

      // Create approval request
      createApprovalRequest: (request: Omit<ApprovalRequest, 'id' | 'requestedAt'>) => {
        const requestId = `approval-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        set(draft => {
          const newRequest: ApprovalRequest = {
            ...request,
            id: requestId,
            requestedAt: new Date(),
          };
          draft.pendingApprovals.push(newRequest);
        });

        return requestId;
      },

      // Process approval
      processApproval: async (requestId: string, approved: boolean, reason?: string) => {
        const { logAdminAction } = get();
        
        set(draft => {
          const request = draft.pendingApprovals.find(r => r.id === requestId);
          if (request) {
            const currentLevel = request.approvalLevels.find(l => !l.approvedBy && !l.denied);
            if (currentLevel) {
              if (approved) {
                currentLevel.approvedBy = draft.securityContext?.adminId || 'unknown';
                currentLevel.approvedAt = new Date();
              } else {
                currentLevel.denied = true;
                currentLevel.deniedReason = reason;
                request.status = 'denied';
              }
            }
            
            // Check if all levels are approved
            const allApproved = request.approvalLevels.every(l => l.approvedBy || !l.required);
            if (allApproved && approved) {
              request.status = 'approved';
              request.completedAt = new Date();
            }
          }
        });

        await logAdminAction(
          approved ? AdminActionType.EMERGENCY_OVERRIDE : AdminActionType.EMERGENCY_OVERRIDE,
          'approval',
          { requestId, approved, reason },
          requestId
        );
      },

      // Emergency override
      emergencyOverride: async (requestId: string, reason: string) => {
        const { logAdminAction, securityContext } = get();
        
        set(draft => {
          const request = draft.pendingApprovals.find(r => r.id === requestId);
          if (request) {
            request.emergencyOverride = {
              triggeredBy: securityContext?.adminId || 'unknown',
              reason,
              timestamp: new Date(),
              requiresPostApproval: true,
            };
            request.status = 'approved';
            request.completedAt = new Date();
          }
        });

        await logAdminAction(AdminActionType.EMERGENCY_OVERRIDE, 'approval', {
          requestId,
          reason,
          overrideType: 'emergency',
        }, requestId);

        toast(`Emergency override activated for request ${requestId}`, {
          duration: 5000,
          icon: '🚨',
        });
      },

      // Trigger system lockdown
      triggerSystemLockdown: async (reason: string) => {
        const { logAdminAction } = get();
        
        await logAdminAction(AdminActionType.SYSTEM_LOCKDOWN, 'system', {
          reason,
          lockdownTime: new Date(),
        });

        toast.error(`System lockdown activated: ${reason}`, {
          duration: 10000,
          icon: '🔒',
        });
      },

      // Emergency refund
      emergencyRefund: async (orderId: string, amount: number, reason: string) => {
        const { logAdminAction } = get();
        
        await logAdminAction(AdminActionType.EMERGENCY_OVERRIDE, 'refund', {
          orderId,
          amount,
          reason,
          emergencyRefund: true,
        }, orderId);

        toast.success(`Emergency refund of $${amount} processed`, {
          duration: 5000,
          icon: '🚨💰',
        });
      },

      // Utility functions
      setLoading: (loading: boolean) => {
        set(draft => {
          draft.isLoading = loading;
        });
      },

      setError: (error: string | null) => {
        set(draft => {
          draft.error = error;
        });
      },

      checkPermission: (category: keyof AdminPermissionMatrix, permission: string) => {
        const { securityContext } = get();
        if (!securityContext) return false;
        
        const categoryPermissions = securityContext.permissions[category] as any;
        return categoryPermissions?.[permission] === true;
      },
    })),
    {
      name: 'nova-admin',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        auditLogs: state.auditLogs,
        performanceMetrics: state.performanceMetrics,
        fraudAlerts: state.fraudAlerts,
        pendingApprovals: state.pendingApprovals,
      }),
    }
  )
);

// Helper functions
function getSeverityForAction(action: AdminActionType): 'low' | 'medium' | 'high' | 'critical' {
  const criticalActions = [AdminActionType.SYSTEM_LOCKDOWN, AdminActionType.EMERGENCY_OVERRIDE];
  const highActions = [AdminActionType.PROCESS_REFUND, AdminActionType.CANCEL_ORDER, AdminActionType.FREEZE_ACCOUNT];
  const mediumActions = [AdminActionType.MODIFY_ORDER, AdminActionType.FLAG_SUSPICIOUS_ACTIVITY];
  
  if (criticalActions.includes(action)) return 'critical';
  if (highActions.includes(action)) return 'high';
  if (mediumActions.includes(action)) return 'medium';
  return 'low';
}

function getCategoryForAction(action: AdminActionType): 'strategic' | 'oversight' | 'operational' | 'emergency' | 'fraud' {
  const strategicActions = [AdminActionType.VIEW_FINANCIAL_REPORT, AdminActionType.MODIFY_SYSTEM_SETTINGS];
  const oversightActions = [AdminActionType.VIEW_STAFF_PERFORMANCE, AdminActionType.CONDUCT_REVIEW];
  const emergencyActions = [AdminActionType.SYSTEM_LOCKDOWN, AdminActionType.EMERGENCY_OVERRIDE];
  const fraudActions = [AdminActionType.FLAG_SUSPICIOUS_ACTIVITY, AdminActionType.FREEZE_ACCOUNT, AdminActionType.INVESTIGATE_TRANSACTION];
  
  if (strategicActions.includes(action)) return 'strategic';
  if (oversightActions.includes(action)) return 'oversight';
  if (emergencyActions.includes(action)) return 'emergency';
  if (fraudActions.includes(action)) return 'fraud';
  return 'operational';
}